import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  TextField,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
} from "@mui/material";
import { motion } from "framer-motion";
import { HiHeart, HiEye, HiMapPin } from "react-icons/hi2";
import AppPagination from "../Tools/AppPagination";
import api from "../../services/api";
import { useDispatch, useSelector } from "react-redux";
import { getBikeNames } from "../../State/NewBike/Action";

export default function UsedBikeList({ modelId = "" }) {
  const [bikes, setBikes] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [model, setModel] = useState(modelId);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedBike, setSelectedBike] = useState(null);
  const [interestedBikes, setInterestedBikes] = useState(new Set());
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const bikeNames = useSelector(
    (store) => store.newBikesResponse.bikeNames.bikeModelNames
  );

  useEffect(() => {
    setPage(0); // Reset to first page when model changes
  }, [model]);

  useEffect(() => {
    dispatch(getBikeNames());

    api
      .get(`/usedBikes?model=${model}&page=${page}`)
      .then((res) => {
        setBikes(res.data.usedBikes);
        setTotalPages(res.data.totalPages);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [page, model]);

  const handleViewMore = (e, bikeId) => {
    navigate(`/usedBikeDetail?bikeId=${bikeId}`);
  };

  const handleInterest = (bike) => {
    setInterestedBikes((prev) => new Set(prev).add(bike.bikeId)); // Disable button immediately

    api
      .post("/interested", { bikeId: bike.bikeId })
      .then((res) => {
        if (res.data.success) {
          setOpenDialog(true);
          setBikes((prevBikes) =>
            prevBikes.map((b) =>
              b.bikeId === bike.bikeId ? { ...b, interested: true } : b
            )
          );
        } else {
          alert(res.data.message);
          setInterestedBikes((prev) => {
            const newSet = new Set(prev);
            newSet.delete(bike.bikeId); // Re-enable button if failed
            return newSet;
          });
        }
      })
      .catch((error) => {
        console.error("Error submitting interest:", error);
        alert("Failed to submit interest. Please try again!");
        setInterestedBikes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(bike.bikeId); // Re-enable button if failed
          return newSet;
        });
      });
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <HiHeart className="w-8 h-8 text-red-500 mr-3" />
            <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
              Used Bikes
            </h1>
          </div>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Find your perfect pre-owned bike with great value and trusted quality
          </p>
        </motion.div>

        {/* Modern Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex justify-center mb-8"
        >
          <Autocomplete
            onChange={(event, value) => setModel(value?.id || "")}
            sx={{
              width: 400,
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
              "& .MuiOutlinedInput-root": {
                borderRadius: "16px",
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#10b981",
                },
              }
            }}
            options={bikeNames}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Used Bikes"
                placeholder="Find your perfect pre-owned ride..."
              />
            )}
          />
        </motion.div>

        {/* Modern Bike Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8"
        >
          {bikes.map((bike, index) => (
            <motion.div
              key={bike.bikeId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="group"
            >
              <Card 
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border-0"
                sx={{ 
                  borderRadius: "16px",
                  "&:hover": {
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
                  }
                }}
              >
                <div className="relative overflow-hidden">
                  <CardMedia
                    component="img"
                    height="200"
                    image={bike.imageUrl}
                    alt={bike.brand + " " + bike.model}
                    onClick={(e) => handleViewMore(e, bike.bikeId)}
                    className="cursor-pointer transition-transform duration-300 group-hover:scale-110"
                    sx={{
                      borderTopLeftRadius: "16px",
                      borderTopRightRadius: "16px",
                    }}
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg">
                    <HiHeart className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="absolute top-4 left-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    Used
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <Typography 
                    variant="h6" 
                    className="font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors"
                    sx={{ fontSize: "1.1rem" }}
                  >
                    {bike.brand} {bike.model}
                  </Typography>
                  
                  <Typography 
                    variant="body2" 
                    className="text-gray-500 mb-3"
                    sx={{ fontSize: "0.9rem" }}
                  >
                    {bike.modelYear} Model
                  </Typography>
                  
                  <div className="flex items-center mb-3">
                    <HiMapPin className="w-4 h-4 text-gray-400 mr-1" />
                    <Typography 
                      variant="body2" 
                      className="text-gray-500"
                      sx={{ fontSize: "0.85rem" }}
                    >
                      {bike.sellerCity}
                    </Typography>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <Typography 
                      variant="h6" 
                      className="font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent"
                      sx={{ fontSize: "1.2rem" }}
                    >
                      ₹{bike.price.toLocaleString()}/-
                    </Typography>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="contained"
                      onClick={(e) => handleViewMore(e, bike.bikeId)}
                      className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                      startIcon={<HiEye className="w-4 h-4" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        py: 1
                      }}
                    >
                      View
                    </Button>

                    <Button
                      variant="contained"
                      onClick={() => handleInterest(bike)}
                      disabled={bike.interested || interestedBikes.has(bike.bikeId)}
                      className={`rounded-xl py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl ${
                        bike.interested || interestedBikes.has(bike.bikeId)
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                      }`}
                      startIcon={<HiHeart className="w-4 h-4" />}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        py: 1,
                        minWidth: "auto",
                        px: 2
                      }}
                    >
                      {bike.interested || interestedBikes.has(bike.bikeId) ? "Interested" : "Interest"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Modern Pagination */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="flex justify-center mt-12"
        >
          <Box className="bg-white rounded-2xl shadow-lg p-4">
            <AppPagination setPage={setPage} totalPages={totalPages} />
          </Box>
        </motion.div>
      </div>

      {/* Modern Interest Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 25px 50px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            textAlign: "center",
            fontSize: "1.5rem",
            color: "#059669",
            py: 3
          }}
        >
          🎉 Interest Submitted Successfully!
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 2 }}>
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <HiHeart className="w-8 h-8 text-green-600" />
            </div>
            <DialogContentText 
              className="text-gray-700 text-center"
              sx={{ fontSize: "1.1rem" }}
            >
              {selectedBike ? (
                <>
                  You have shown interest in{" "}
                  <strong className="text-green-600">
                    {selectedBike.brand} {selectedBike.model}
                  </strong>
                  . Our dealer will reach out to you shortly with more details.
                </>
              ) : (
                "Our team or dealer will contact you soon with further details!"
              )}
            </DialogContentText>
          </div>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", p: 3 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            variant="contained"
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl px-6 py-2 font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
            sx={{
              borderRadius: "12px",
              textTransform: "none",
              px: 4,
              py: 1
            }}
          >
            Got it!
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
